import { useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { History, Play, SlidersHorizontal } from 'lucide-react'
import { testsApi } from '@/api/tests'
import { vocabularyApi } from '@/api/vocabulary'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { getApiErrorMessage } from '@/lib/apiError'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/components/ui/cn'
import { useSettingsStore } from '@/store/settingsStore'
import type { CefrLevel } from '@/types/common'
import type { GeneratedTest, QuestionType } from '@/types/test'

const QUESTION_COUNT_OPTIONS = [10, 20, 30]

const QUESTION_TYPE_OPTIONS: { value: QuestionType; label: string }[] = [
  { value: 'MULTIPLE_CHOICE', label: 'Multiple choice' },
  { value: 'TYPING', label: 'Typing' },
  { value: 'FILL_BLANK', label: 'Fill in blank' },
  { value: 'MATCHING', label: 'Matching' },
]

const CEFR_OPTIONS: { value: CefrLevel | ''; label: string }[] = [
  { value: '', label: 'All levels' },
  { value: 'A1', label: 'A1' },
  { value: 'A2', label: 'A2' },
  { value: 'B1', label: 'B1' },
  { value: 'B2', label: 'B2' },
  { value: 'C1', label: 'C1' },
  { value: 'C2', label: 'C2' },
]

function OptionPill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
          : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-secondary/60'
      )}
    >
      {children}
    </button>
  )
}

function scoreTone(score: number) {
  if (score >= 80) return 'text-green-500'
  if (score >= 50) return 'text-amber-500'
  return 'text-muted-foreground'
}

export function TestsPage() {
  const navigate = useNavigate()
  const [questionCount, setQuestionCount] = useState(10)
  const [questionTypes, setQuestionTypes] = useState<QuestionType[]>(['MULTIPLE_CHOICE', 'TYPING'])
  const [cefrLevel, setCefrLevel] = useState<CefrLevel | ''>('')
  const [collectionId, setCollectionId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const nativeLanguage = useSettingsStore((s) => s.settings?.nativeLanguage ?? 'en')

  const collectionsQuery = useQuery({
    queryKey: ['collections', nativeLanguage],
    queryFn: () => vocabularyApi.getCollections().then((r) => r.data),
  })

  const historyQuery = useQuery({
    queryKey: ['testHistory'],
    queryFn: () => testsApi.getHistory(0, 10),
  })

  const generateMutation = useMutation({
    mutationFn: () =>
      testsApi.generateTest({
        testType: 'VOCABULARY',
        questionCount,
        questionTypes,
        cefrLevel: cefrLevel || undefined,
        collectionId: collectionId || undefined,
      }),
    onSuccess: (test: GeneratedTest) => {
      navigate(`/tests/${test.id}`, { state: { test } })
    },
    onError: (err: unknown) => {
      setError(
        getApiErrorMessage(
          err,
          'Failed to generate test. Add more words to your vocabulary first.'
        )
      )
    },
  })

  const toggleQuestionType = (type: QuestionType) => {
    setQuestionTypes((current) =>
      current.includes(type)
        ? current.length > 1
          ? current.filter((value) => value !== type)
          : current
        : [...current, type]
    )
  }

  const history = historyQuery.data?.content ?? []

  return (
    <div className="space-y-8">
      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <Card className="overflow-hidden">
          <div className="border-b border-border bg-secondary/30 px-6 py-4">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-primary" />
              <h2 className="font-semibold text-foreground">Start a vocabulary test</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Questions mix English → translation and translation → English automatically.
            </p>
          </div>

          <div className="space-y-7 p-6">
            <div>
              <label className="text-sm font-medium text-foreground">Question count</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {QUESTION_COUNT_OPTIONS.map((count) => (
                  <OptionPill key={count} active={questionCount === count} onClick={() => setQuestionCount(count)}>
                    {count}
                  </OptionPill>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Question types</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {QUESTION_TYPE_OPTIONS.map((option) => (
                  <OptionPill
                    key={option.value}
                    active={questionTypes.includes(option.value)}
                    onClick={() => toggleQuestionType(option.value)}
                  >
                    {option.label}
                  </OptionPill>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Fill-in-blank shows your translation as a hint and lets you pick the English word.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cefr-filter" className="text-sm font-medium text-foreground">
                  CEFR level
                </label>
                <select
                  id="cefr-filter"
                  value={cefrLevel}
                  onChange={(event) => setCefrLevel(event.target.value as CefrLevel | '')}
                  className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm"
                >
                  {CEFR_OPTIONS.map((option) => (
                    <option key={option.label} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="collection-filter" className="text-sm font-medium text-foreground">
                  Collection
                </label>
                <select
                  id="collection-filter"
                  value={collectionId}
                  onChange={(event) => setCollectionId(event.target.value)}
                  className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm"
                >
                  <option value="">All words</option>
                  {(collectionsQuery.data ?? []).map((collection) => (
                    <option key={collection.id} value={collection.id}>
                      {collection.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <Button
              onClick={() => {
                setError(null)
                generateMutation.mutate()
              }}
              disabled={generateMutation.isPending || questionTypes.length === 0}
              className="w-full gap-2 sm:w-auto"
            >
              {generateMutation.isPending ? <LoadingSpinner size="sm" /> : <Play className="h-4 w-4" />}
              Start test
            </Button>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="border-b border-border bg-secondary/30 px-6 py-4">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-foreground">Test history</h2>
            </div>
          </div>

          <div className="p-6">
            {historyQuery.isLoading ? (
              <div className="flex justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : history.length === 0 ? (
              <EmptyState
                title="No tests yet"
                description="Complete your first vocabulary test to see results here."
              />
            ) : (
              <div className="space-y-2">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between rounded-lg border border-border/70 px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {new Date(item.completedAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.correctAnswers}/{item.totalQuestions} correct
                      </p>
                    </div>
                    <p className={cn('text-lg font-semibold tabular-nums', scoreTone(item.scorePercent))}>
                      {Math.round(item.scorePercent)}%
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
